import React, { type Ref } from 'react';

import { FastshiftRunbookStep } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ShowMoreVerticalIcon from '@atlaskit/icon/core/show-more-vertical';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';

import { messages } from '../messages';

import { isRunbookStepDone } from './runbook-step-list-utils';

export type RunbookStepActionsMenuProps = {
	step: FastshiftRunbookStep;
	isPending: boolean;
	onMenuOpen: () => void;
	onToggleDisabled: () => void;
	onToggleRisk: () => void;
	onToggleStatus: () => void;
};

const stepActionsStyles = xcss({
	flexShrink: 0,
});

const LoadingStepActionsIcon = () => (
	<Spinner size="small" label="" testId="runbook-step-actions-loading" />
);

export const RunbookStepActionsMenu: React.FC<RunbookStepActionsMenuProps> = ({
	step,
	isPending,
	onMenuOpen,
	onToggleDisabled,
	onToggleRisk,
	onToggleStatus,
}) => {
	const intl = useIntl();

	return (
		<Box xcss={stepActionsStyles}>
			<DropdownMenu
				onOpenChange={({ isOpen }) => {
					if (isOpen) {
						onMenuOpen();
					}
				}}
				// This is atlaskit's recommended pattern for custom trigger components
				// eslint-disable-next-line react/no-unstable-nested-components
				trigger={({ triggerRef, ...triggerProps }) => (
					<IconButton
						ref={triggerRef as Ref<HTMLButtonElement>}
						{...triggerProps}
						appearance="subtle"
						spacing="compact"
						isDisabled={isPending}
						label={intl.formatMessage(messages.runbooksPanelStepActionsMenuLabel)}
						icon={isPending ? LoadingStepActionsIcon : ShowMoreVerticalIcon}
						testId={`runbook-step-actions-${step.stepId}`}
					/>
				)}
				spacing="compact"
				shouldRenderToParent
			>
				<DropdownItemGroup>
					<DropdownItem onClick={onToggleDisabled}>
						{intl.formatMessage(
							step.disabled
								? messages.runbooksPanelStepEnableAction
								: messages.runbooksPanelStepDisableAction,
						)}
					</DropdownItem>
					{/* A disabled step can only be re-enabled; its status and
                        risk flag can't be changed until it's enabled again. */}
					<DropdownItem onClick={onToggleRisk} isDisabled={step.disabled}>
						{intl.formatMessage(
							step.risk
								? messages.runbooksPanelStepUnflagAction
								: messages.runbooksPanelStepFlagAction,
						)}
					</DropdownItem>
					<DropdownItem onClick={onToggleStatus} isDisabled={step.disabled}>
						{intl.formatMessage(
							isRunbookStepDone(step)
								? messages.runbooksPanelStepMarkIncompleteAction
								: messages.runbooksPanelStepMarkCompleteAction,
						)}
					</DropdownItem>
				</DropdownItemGroup>
			</DropdownMenu>
		</Box>
	);
};
